import { useState, useEffect } from 'react';
import { getStagedVoyageDiff } from '../services/StagingRepository';
import useStagingCandidates from './useStagingCandidates';
import useStagingMerge from './useStagingMerge';
import useStagingActions from './useStagingActions';

/**
 * Custom hook encapsulating all staging review modal state + async operations.
 * Composed from useStagingCandidates, useStagingMerge, and useStagingActions.
 */
const useStagingReview = ({ open, stagedVoyage, onResolved, onClose }) => {
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [view, setView] = useState('candidates'); // 'candidates' | 'diff'

  const candidateHook = useStagingCandidates({ open, stagedVoyage });
  const mergeHook = useStagingMerge({ diffData });
  const actionHook = useStagingActions({ stagedVoyage, onResolved, onClose });

  // Reset view state when modal opens
  useEffect(() => {
    if (open && stagedVoyage) {
      setView('candidates');
      setSelectedCandidate(null);
      setDiffData(null);
      mergeHook.resetSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stagedVoyage]);

  const handleViewDiff = async (candidate) => {
    setSelectedCandidate(candidate);
    setLoadingDiff(true);
    mergeHook.resetSelections();
    try {
      const diff = await getStagedVoyageDiff(
        stagedVoyage.id,
        candidate.voyageId
      );
      setDiffData(diff);
      setView('diff');
    } catch {
      actionHook.snackbar.open = true; // fallback — use snackbar from actions
    } finally {
      setLoadingDiff(false);
    }
  };

  const handleConfirmMatch = () =>
    actionHook.handleConfirmMatch({
      selectedCandidate,
      mergeMode: mergeHook.mergeMode,
      hasAnySelection: mergeHook.hasAnySelection,
      fieldSelections: mergeHook.fieldSelections,
      shipmentSelections: mergeHook.shipmentSelections,
      shipmentFieldSelections: mergeHook.shipmentFieldSelections,
    });

  const goBackToCandidates = () => {
    setView('candidates');
    mergeHook.resetSelections();
  };

  return {
    // Candidates
    candidates: candidateHook.candidates,
    loadingCandidates: candidateHook.loadingCandidates,
    searchQuery: candidateHook.searchQuery,
    setSearchQuery: candidateHook.setSearchQuery,
    loadingSearch: candidateHook.loadingSearch,
    handleSearch: candidateHook.handleSearch,

    // Diff view
    selectedCandidate,
    diffData,
    loadingDiff,
    view,
    handleViewDiff,
    goBackToCandidates,

    // Merge selections
    mergeMode: mergeHook.mergeMode,
    fieldSelections: mergeHook.fieldSelections,
    shipmentSelections: mergeHook.shipmentSelections,
    shipmentFieldSelections: mergeHook.shipmentFieldSelections,
    hasAnySelection: mergeHook.hasAnySelection,
    selectionSummary: mergeHook.selectionSummary,
    handleFieldSelectionChange: mergeHook.handleFieldSelectionChange,
    handleShipmentSelectionChange: mergeHook.handleShipmentSelectionChange,
    handleShipmentFieldSelectionChange:
      mergeHook.handleShipmentFieldSelectionChange,
    setMergeModeValue: mergeHook.setMergeModeValue,
    resetSelections: mergeHook.resetSelections,

    // Actions
    actionLoading: actionHook.actionLoading,
    snackbar: actionHook.snackbar,
    closeSnackbar: actionHook.closeSnackbar,
    handleConfirmMatch,
    handleCreateAsNew: actionHook.handleCreateAsNew,
    handleReject: actionHook.handleReject,
  };
};

export default useStagingReview;
